/**
 * DateInput — single masked text field for birth-date entry (TT.MM.JJJJ).
 *
 * The user types only digits; dots are inserted automatically:
 *   "2" → "2"
 *   "22" → "22"
 *   "220" → "22.0"
 *   "2207" → "22.07"
 *   "22072" → "22.07.2"
 *   "22072025" → "22.07.2025"
 *
 * Backspace through auto-inserted dots works naturally: deleting a "."
 * also removes the preceding digit so the cursor never gets stuck on a
 * separator.
 *
 * Value contract: emits "DD.MM.YYYY" once fully entered, partial strings
 * while typing. parseDob() in chart.ts accepts "DD.MM.YYYY".
 */

import { useRef } from "react";

type Props = {
  id: string;
  /** Current formatted value, e.g. "22.07.2025" or partial "22.0". */
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  label?: string;
  hint?: string;
};

/** Insert dots after DD and MM positions. */
function applyDateMask(digits: string): string {
  const d = digits.slice(0, 8);
  if (d.length > 4) return `${d.slice(0, 2)}.${d.slice(2, 4)}.${d.slice(4)}`;
  if (d.length > 2) return `${d.slice(0, 2)}.${d.slice(2)}`;
  return d;
}

const MAX_YEAR = new Date().getFullYear() + 1;

function validate(v: string): string | null {
  const parts = v.split(".");
  if (parts.length !== 3 || parts[2].length !== 4) return null; // only validate when complete
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return "Ungültiges Datum";
  if (day < 1 || day > 31) return "Tag 01–31";
  if (month < 1 || month > 12) return "Monat 01–12";
  if (year < 1900 || year > MAX_YEAR) return `Jahr 1900–${MAX_YEAR}`;
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day > daysInMonth) return "Ungültiger Tag für diesen Monat";
  return null;
}

/** "DD.MM.YYYY" → "YYYY-MM-DD" (returns "" if not a full valid date). */
export function toIsoDate(ddmmyyyy: string): string {
  const parts = ddmmyyyy.split(".");
  if (parts.length !== 3 || parts[2].length !== 4) return "";
  return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
}

export function DateInput({
  id,
  value,
  onChange,
  required = false,
  label = "Geburtsdatum (TT.MM.JJJJ)",
  hint,
}: Props) {
  // Track the last emitted value to detect separator-deletion on backspace.
  const prevRef = useRef(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const prev = prevRef.current;

    const newDigits = raw.replace(/\D/g, "");
    const prevDigits = prev.replace(/\D/g, "");

    let digits = newDigits.slice(0, 8);

    // Backspace on a separator: digit count unchanged but string shrank →
    // remove one extra digit so the separator disappears instead of
    // being immediately re-inserted.
    if (newDigits.length === prevDigits.length && raw.length < prev.length) {
      digits = newDigits.slice(0, -1);
    }

    const masked = applyDateMask(digits);
    prevRef.current = masked;
    onChange(masked);
  };

  const isComplete = value.length === 10; // "DD.MM.YYYY"
  const error = isComplete ? validate(value) : null;

  return (
    <div className="flex flex-col gap-1.5 text-left">
      <label htmlFor={id} className="text-xs font-medium tracking-wide text-charcoal/60">
        {label}{required ? " *" : ""}
      </label>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="TT.MM.JJJJ"
        maxLength={10}
        value={value}
        onChange={handleChange}
        className={`w-full rounded-xl border bg-white/90 px-3 py-2.5 font-mono text-charcoal outline-none transition-colors focus:bg-white placeholder:font-sans placeholder:text-slate-300 ${
          error
            ? "border-red-400 focus:border-red-400"
            : "border-slate-200 focus:border-gold"
        }`}
      />
      <p className={`text-xs ${error ? "text-red-500" : "text-charcoal/40"}`}>
        {error ?? (hint ?? "Format: Tag . Monat . Jahr")}
      </p>
    </div>
  );
}
